import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Phone, Mail, Globe, Save, AlertCircle } from 'lucide-react';
import { VENDOR_CATEGORIES, PRICE_RANGE_LABELS } from '@/types/vendor';
import { VendorProfile, useUpdateVendorProfile } from '@/hooks/useVendor';

interface VendorProfileTabProps {
  vendor: VendorProfile;
}

const VendorProfileTab = ({ vendor }: VendorProfileTabProps) => {
  const updateProfile = useUpdateVendorProfile();
  
  const [formData, setFormData] = useState({
    name: vendor.name || '',
    category: vendor.category || 'venue',
    description: vendor.description || '',
    short_description: vendor.short_description || '',
    location: vendor.location || '',
    price_range: vendor.price_range || 'moderate',
    contact_email: vendor.contact_email || '',
    contact_phone: vendor.contact_phone || '',
    website: vendor.website || '',
  });

  useEffect(() => {
    setFormData({
      name: vendor.name || '',
      category: vendor.category || 'venue',
      description: vendor.description || '',
      short_description: vendor.short_description || '',
      location: vendor.location || '',
      price_range: vendor.price_range || 'moderate',
      contact_email: vendor.contact_email || '',
      contact_phone: vendor.contact_phone || '',
      website: vendor.website || '',
    });
  }, [vendor]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate(formData);
  };

  const categoryInfo = VENDOR_CATEGORIES.find(c => c.id === vendor.category);

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {!vendor.is_approved && (
        <Card className="border-accent bg-accent/5">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-accent" />
            <div>
              <p className="font-medium text-foreground">Perfil en revisión</p>
              <p className="text-sm text-muted-foreground">
                Tu perfil está siendo revisado por nuestro equipo. Una vez aprobado, aparecerás en el directorio.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Información Básica</CardTitle>
            <CardDescription>
              Datos principales de tu negocio que verán las parejas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del Negocio</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categoría</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="price_range">Rango de Precios</Label>
                <Select
                  value={formData.price_range}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, price_range: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRICE_RANGE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Ubicación</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="location"
                    className="pl-10"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Ciudad, País"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="short_description">Descripción Corta</Label>
              <Input
                id="short_description"
                value={formData.short_description}
                onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                placeholder="Una frase que resuma tu negocio"
                maxLength={150}
              />
              <p className="text-xs text-muted-foreground">
                {formData.short_description.length}/150 caracteres
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción Completa</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe tu negocio, servicios, experiencia..."
                rows={5}
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Información de Contacto</CardTitle>
            <CardDescription>
              Cómo pueden contactarte las parejas interesadas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="contact_email"
                    type="email"
                    className="pl-10"
                    value={formData.contact_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_phone">Teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="contact_phone"
                    type="tel"
                    className="pl-10"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="website">Sitio Web</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="website"
                    type="url"
                    className="pl-10"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://www.tunegocio.com"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Vista Previa</CardTitle>
            <CardDescription>
              Así se verá tu tarjeta en el directorio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-sm mx-auto">
              <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-soft">
                <div className="h-40 bg-muted flex items-center justify-center">
                  {vendor.image ? (
                    <img src={vendor.image} alt={formData.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl">{categoryInfo?.icon || '🏢'}</span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary">{categoryInfo?.name || formData.category}</Badge>
                    <Badge variant="outline">{PRICE_RANGE_LABELS[formData.price_range as keyof typeof PRICE_RANGE_LABELS] || '€€'}</Badge>
                  </div>
                  <h3 className="font-serif font-semibold text-lg">{formData.name || 'Nombre del negocio'}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {formData.short_description || 'Descripción breve de tu negocio...'}
                  </p>
                  {formData.location && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {formData.location}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={updateProfile.isPending} className="gap-2">
            <Save className="h-4 w-4" />
            {updateProfile.isPending ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VendorProfileTab;
